import React, { useEffect, useState, useRef, useMemo } from 'react';
import { BREAKPOINTS } from '../constants.js';
import { Icons } from './Icon.jsx';
import { BEHAVIORS, getBehavior } from './behaviors/registry.js';
import { generateId, calculateRect, calculateLayout, measureNode } from '../utils.js';
import { resolveNode } from '../utils/responsive.js';
import VariantEditorDialog from './VariantEditorDialog/index.jsx';
import { DraggableLabel } from './DraggableLabel.jsx';
import ResponsivePanel from './PropertyPanel/ResponsivePanel.jsx';
import PropertySection from './PropertyPanel/PropertySection.jsx';
import BehaviorItem from './PropertyPanel/BehaviorItem.jsx';
import BindingPopup from './PropertyPanel/renderers/BindingPopup.jsx';
import RectTransform from './PropertyPanel/renderers/RectTransform.jsx';
import InstanceVariables from './PropertyPanel/renderers/InstanceVariables.jsx';
import FieldRenderer from './PropertyPanel/renderers/FieldRenderer.jsx';
import PrefabPanel from './PropertyPanel/PrefabPanel.jsx';

const OBJECT_STYLES = {
    backgroundColor: { label: 'Background', type: 'color', default: '' },
    className: { label: 'Classes', type: 'text', default: '' },
    overflow: { label: 'Overflow', type: 'select', options: ['visible', 'hidden', 'scroll', 'auto'], default: 'visible' },
    fontSize: { label: 'Font Size', type: 'number', default: 16 },
    fontWeight: { label: 'Font Weight', type: 'select', options: ['normal', 'bold', 'lighter'], default: 'normal' },
    color: { label: 'Color', type: 'color', default: '' },
    horizontalAlign: { label: 'Horizontal Align', type: 'h_align', default: '' },
    verticalAlign: { label: 'Vertical Align', type: 'v_align', default: '' },
};

const PropertyPanel = ({ controller }) => {
  const {
    selectedNodes,
    parentSize,
    onChange,
    onDelete,
    onMakePrefab,
    onDetachPrefab,
    onUpdatePrefab,
    onOpenPrefab,
    activeNodes,
    onSelect,
    prefabs,
    activeBreakpoint,
    effectiveBreakpoint,
    onBreakpointChange,
    onViewportWidthChange,
    settings,
    containerNode,
    canvasRef,
    canvasScale
  } = controller;
  const [modifiers, setModifiers] = useState({ shift: false, alt: false });
  const [showAddComponent, setShowAddComponent] = useState(false);
  const [clipboardBehavior, setClipboardBehavior] = useState(null);
  const [variantEditor, setVariantEditor] = useState({ isOpen: false, componentType: null });
  const [stylingTab, setStylingTab] = useState('variant');
  const [showAddObjectStyle, setShowAddObjectStyle] = useState(false);
  const [objectStyleSearch, setObjectStyleSearch] = useState('');

  // Binding State
  const [bindingPopup, setBindingPopup] = useState(null);

  const [bindingSearch, setBindingSearch] = useState("");

  const rawPrimaryNode = selectedNodes.length > 0 ? selectedNodes[selectedNodes.length - 1] : null;
  const primaryNode = useMemo(() => {
      return rawPrimaryNode ? resolveNode(rawPrimaryNode, effectiveBreakpoint) : null;
  }, [rawPrimaryNode, effectiveBreakpoint]);

  // Calculate Effective Parent Size for RectTransform
  const effectiveParentSize = useMemo(() => {
      if (!primaryNode || !primaryNode.parentId) return parentSize;

      // We must measure the parent to get its resolved dimensions
      // measureNode traverses up and resolves nodes if we pass effectiveBreakpoint
      const rect = measureNode(primaryNode.parentId, activeNodes, parentSize.width, parentSize.height, effectiveBreakpoint);
      return { width: rect.width, height: rect.height };
  }, [primaryNode, activeNodes, parentSize, effectiveBreakpoint]);

  useEffect(() => {
    const handleKey = (e) => setModifiers({ shift: e.shiftKey, alt: e.altKey });
    window.addEventListener('keydown', handleKey); window.addEventListener('keyup', handleKey);
    return () => { window.removeEventListener('keydown', handleKey); window.removeEventListener('keyup', handleKey); };
  }, []);

  if (!primaryNode) return <div className="p-4 text-gray-500 text-sm text-center italic">Select an element</div>;

  const isPrefab = !!primaryNode.props?.prefabId;
  const isVirtual = !!primaryNode.isVirtual;
  const targetInstanceId = isVirtual ? primaryNode.instanceId : primaryNode.id;

  // --- Data Binding Logic ---

  const getContextRoot = () => {
      // Only applicable when drilled into a container that is NOT in the main active nodes
      const isSwappedContext = !activeNodes.some(n => n.id === containerNode?.id);
      if (!containerNode || !isSwappedContext) return null;
      return activeNodes.find(n => !n.parentId);
  };

  const contextRoot = getContextRoot();
  const dataBindingComp = contextRoot?.attachedComponents?.find(c => c.type === 'DataBinding');
  const isBindingEnabled = !!dataBindingComp;

  const getBindings = () => {
      return (dataBindingComp?.props.bindings) || [];
  };

  const updateRootBindings = (newBindings) => {
      if (!contextRoot || !dataBindingComp) return;
      const newComps = contextRoot.attachedComponents?.map(c =>
        c.id === dataBindingComp.id ? { ...c, props: { ...c.props, bindings: newBindings } } : c
      ) || [];
      onChange({ attachedComponents: newComps }, [contextRoot.id]);
  };

  const handleBind = (externalKey) => {
      if (!bindingPopup) return;
      const { nodeId, componentId, property } = bindingPopup;
      const currentBindings = getBindings();

      const filtered = currentBindings.filter(b => !(b.nodeId === nodeId && b.componentId === componentId && b.property === property));

      const newBinding = {
          id: generateId('bind'),
          nodeId, componentId, property, externalKey
      };

      updateRootBindings([...filtered, newBinding]);
      setBindingPopup(null);
  };

  const handleUnbind = (bindingId) => {
      if (bindingId) {
           updateRootBindings(getBindings().filter(b => b.id !== bindingId));
      } else if (bindingPopup) {
           const { nodeId, componentId, property } = bindingPopup;
           updateRootBindings(getBindings().filter(b => !(b.nodeId === nodeId && b.componentId === componentId && b.property === property)));
           setBindingPopup(null);
      }
  };

  // --- Responsive Write Helpers ---

  const handleResponsiveChange = (changes, nodeIds) => {
      // If default, handle null values (for removing properties)
      if (activeBreakpoint === 'default') {
          if (typeof changes === 'object' && changes !== null && !Array.isArray(changes)) {
              // Convert null values to undefined for proper deletion
              const cleanedChanges = {};
              for (const [key, value] of Object.entries(changes)) {
                  if (key === 'props' && typeof value === 'object' && value !== null) {
                      // Clean nested props object
                      const cleanedProps = {};
                      for (const [propKey, propValue] of Object.entries(value)) {
                          cleanedProps[propKey] = propValue === null ? undefined : propValue;
                      }
                      cleanedChanges[key] = cleanedProps;
                  } else {
                      cleanedChanges[key] = value === null ? undefined : value;
                  }
              }
              onChange(cleanedChanges, nodeIds);
          } else {
              onChange(changes, nodeIds);
          }
          return;
      }

      if (typeof changes === 'function') {
          onChange(changes, nodeIds);
          return;
      }

      // Separate transform props, standard props, and attached components
      const { x, y, width, height, anchors, props, attachedComponents, ...rest } = changes;

      const overridePatch = {};

      // Global props that should NEVER be overridden per-breakpoint (they handle their own responsiveness)
      const globalProps = {};
      if (props && 'variant' in props) {
          globalProps.variant = props.variant;
          delete props.variant;
      }
      if (props && '_variantUpdate' in props) {
          globalProps._variantUpdate = props._variantUpdate;
          delete props._variantUpdate;
      }

      // 1. Transform Overrides
      if (x !== undefined) overridePatch.layout_x = x;
      if (y !== undefined) overridePatch.layout_y = y;
      if (width !== undefined) overridePatch.layout_width = width;
      if (height !== undefined) overridePatch.layout_height = height;
      if (anchors !== undefined) overridePatch.layout_anchors = anchors;

      // Add props to overridePatch, including undefined (for deletion)
      if (props) {
          Object.entries(props).forEach(([key, value]) => {
              overridePatch[key] = value;
          });
      }

      // We only proceed if there's something to update
      // Allow proceeding even with undefined values in props (for deletion)
      const hasChanges = Object.keys(overridePatch).length > 0 || Object.keys(rest).length > 0 || Object.keys(globalProps).length > 0;
      if (!hasChanges) return;

      onChange((node) => {
          let newNode = { ...node };

          // 1. Apply Global Changes (rest + globalProps)
          const globalChanges = { ...rest, ...((props && Object.keys(props).length === 0) ? {} : {}), ...globalProps };

          if (Object.keys(globalChanges).length > 0) {
              if (globalChanges.props) {
                  // Merge props if they exist in globalChanges (unlikely with current logic but safe)
                   newNode.props = { ...newNode.props, ...globalChanges.props };
                   delete globalChanges.props;
              }
              // Merge top-level global props (variant is inside props usually)
              // Wait, globalProps.variant needs to go into newNode.props.variant
              if ('variant' in globalProps || '_variantUpdate' in globalProps) {
                  newNode.props = { ...newNode.props, ...globalProps };

                  // CRITICAL FIX: If we update a global prop like 'variant', we MUST clear any
                  // responsive override for it in the current breakpoint to ensure the global change is visible.
                  if (activeBreakpoint !== 'default') {
                      const currentOverrides = newNode.props.responsiveOverrides || {};
                      const currentBpOverrides = currentOverrides[activeBreakpoint] || {};

                      if ('variant' in globalProps && 'variant' in currentBpOverrides) {
                          const { variant: _, ...cleanedOverrides } = currentBpOverrides;
                          newNode.props.responsiveOverrides = {
                              ...currentOverrides,
                              [activeBreakpoint]: cleanedOverrides
                          };
                      }
                  }
              }

              newNode = { ...newNode, ...globalChanges };
          }

          // 2. Apply Responsive Overrides
          if (Object.keys(overridePatch).length > 0) {
              const currentOverrides = newNode.props.responsiveOverrides || {};
              const currentBpOverrides = currentOverrides[activeBreakpoint] || {};

              // Merge overrides, but remove keys with undefined/null values (they're being reset)
              const updatedBpOverrides = { ...currentBpOverrides, ...overridePatch };

              // Remove undefined/null values to "reset" properties to base
              Object.keys(updatedBpOverrides).forEach(key => {
                  if (updatedBpOverrides[key] === undefined || updatedBpOverrides[key] === null) {
                      delete updatedBpOverrides[key];
                  }
              });

              newNode.props = {
                  ...newNode.props,
                  responsiveOverrides: {
                      ...currentOverrides,
                      [activeBreakpoint]: updatedBpOverrides
                  }
              };
          }

          return newNode;
      }, nodeIds);
  };

  // --- Prop Handlers ---

  const handlePropChange = (key, value) => {
    handleResponsiveChange(
        key === 'x' || key === 'y' || key === 'width' || key === 'height' || key === 'anchors'
        ? { [key]: value }
        : { props: { [key]: value } },
        selectedNodes.map(n => n.id)
    );
  };

  const handleAddComponent = (type) => {
    // Structural changes remain global
    const def = BEHAVIORS[type];
    if (!def) return;

    // Logic: Only allow adding DataBinding to the Root Node of a Swapped Context (e.g. drilled into a prefab)
    if (type === 'DataBinding') {
         const isSwappedContext = !activeNodes.some(n => n.id === containerNode?.id);
         if (!containerNode || !isSwappedContext) { alert("DataBinding can only be added when drilled into a container."); return; }
         if (primaryNode.parentId) { alert("DataBinding can only be added to the Root of the context."); return; }
    }

    onChange((node) => {
        const defaultProps = {};
        def.fields.forEach(f => defaultProps[f.name] = f.default);
        const newComp = { id: generateId('Comp'), type, props: defaultProps };
        const current = node.attachedComponents || [];
        return { attachedComponents: [...current, newComp] };
    }, selectedNodes.map(n => n.id));
  };

  const handleRemoveComponent = (compId) => {
    // Structural changes remain global
    const current = rawPrimaryNode.attachedComponents || [];
    onChange({ attachedComponents: current.filter(c => c.id !== compId) }, [rawPrimaryNode.id]);
  };

  const handleCompPropChange = (compId, propName, value) => {
    if (activeBreakpoint === 'default') {
        // Default behavior
        const primaryComp = rawPrimaryNode.attachedComponents?.find(c => c.id === compId);
        if (!primaryComp) return;
        const targetType = primaryComp.type;

        onChange((node) => {
            const currentComps = node.attachedComponents || [];
            const updatedComps = currentComps.map(c =>
                c.type === targetType
                ? { ...c, props: { ...c.props, [propName]: value } }
                : c
            );
            return { attachedComponents: updatedComps };
        }, selectedNodes.map(n => n.id));
    } else {
        // Override behavior
        // Find the component index to update
        const primaryComp = rawPrimaryNode.attachedComponents?.find(c => c.id === compId);
        if (!primaryComp) return;

        onChange((node) => {
            const currentComps = node.attachedComponents || [];
            const updatedComps = currentComps.map(c => {
                if (c.id !== compId) return c;

                const currentOverrides = c.props.responsiveOverrides || {};
                const currentBpOverrides = currentOverrides[activeBreakpoint] || {};

                return {
                    ...c,
                    props: {
                        ...c.props,
                        responsiveOverrides: {
                            ...currentOverrides,
                            [activeBreakpoint]: {
                                ...currentBpOverrides,
                                [propName]: value
                            }
                        }
                    }
                };
            });
            return { attachedComponents: updatedComps };
        }, selectedNodes.map(n => n.id));
    }
  };

  const handleCopyBehavior = (comp) => {
      setClipboardBehavior({ type: comp.type, props: { ...comp.props } });
  };

  const handlePasteBehavior = () => {
      if (!clipboardBehavior) return;
      const { type, props } = clipboardBehavior;
      const def = BEHAVIORS[type];
      if (!def) return;

      onChange((node) => {
          const current = node.attachedComponents || [];
          const existing = current.find(c => c.type === type);

          if (existing) {
              // Update existing behavior with pasted values
              const updatedComps = current.map(c =>
                  c.id === existing.id
                  ? { ...c, props: { ...c.props, ...props } }
                  : c
              );
              return { attachedComponents: updatedComps };
          } else {
              // Add new behavior
              const newComp = { id: generateId('Comp'), type, props: { ...props } };
              return { attachedComponents: [...current, newComp] };
          }
      }, selectedNodes.map(n => n.id));
  };

  const handleToggleEnable = (compId, enabled) => {
      handleCompPropChange(compId, 'enabled', enabled);
  };

  const handleApplyVariants = (keys, variantComponentType) => {
      // Simply replace with the new key(s) from the variant editor
      const newVariants = keys.filter(Boolean).join(' ');

      // IMPORTANT: Variants should always be applied to BASE props, not responsive overrides
      // We need to clear any responsive override for 'variant' on the current breakpoint
      // and set it on the base props

      const timestamp = Date.now();
      const propsToUpdate = {
          variant: newVariants,
          _variantUpdate: timestamp
      };

      if (variantComponentType) {
          propsToUpdate.variantComponent = variantComponentType;
      } else if (variantEditor.componentType) {
          propsToUpdate.variantComponent = variantEditor.componentType;
      }

      // Clear variant from responsive overrides if it exists
      if (activeBreakpoint !== 'default') {
          const currentOverrides = rawPrimaryNode?.props?.responsiveOverrides?.[activeBreakpoint] || {};
          if ('variant' in currentOverrides) {
              // Remove variant from this breakpoint's overrides
              const { variant: _, ...cleanedOverrides } = currentOverrides;
              propsToUpdate.responsiveOverrides = {
                  ...rawPrimaryNode?.props?.responsiveOverrides,
                  [activeBreakpoint]: cleanedOverrides
              };
          }
      }

      onChange({ props: propsToUpdate }, selectedNodes.map(n => n.id));

      setVariantEditor({ isOpen: false, componentType: null });
  };

  // --- Renderers ---

  const renderFieldWrapper = (label, value, setter, type, options, fieldName, compId, step, defId, disabledList, setDisabledList) => {
      // Check for overrides
      let isOverridden = false;
      if (activeBreakpoint !== 'default') {
          if (compId) {
              const comp = rawPrimaryNode.attachedComponents?.find(c => c.id === compId);
              isOverridden = comp?.props?.responsiveOverrides?.[activeBreakpoint]?.[fieldName] !== undefined;
          } else {
              // Node props or transform
              if (fieldName === 'x' || fieldName === 'y' || fieldName === 'width' || fieldName === 'height' || fieldName === 'anchors') {
                  const layoutKey = `layout_${fieldName}`;
                  isOverridden = rawPrimaryNode.props?.responsiveOverrides?.[activeBreakpoint]?.[layoutKey] !== undefined;
              } else {
                  isOverridden = rawPrimaryNode.props?.responsiveOverrides?.[activeBreakpoint]?.[fieldName] !== undefined;
              }
          }
      }

      return (
      <FieldRenderer
          key={fieldName || label}
          label={label}
          value={value}
          onChange={setter}
          type={type}
          options={options}
          fieldName={fieldName}
          compId={compId}
          step={step}
          componentType={primaryNode.attachedComponents?.some(c => c.type === 'Text') ? 'Typography' : 'Container'}
          disabledList={disabledList}
          onUpdateDisabled={setDisabledList}
          primaryNode={primaryNode}
          isBindingEnabled={isBindingEnabled}
          bindings={getBindings()}
          onBindingClick={setBindingPopup}
          onVariantEdit={setVariantEditor}
          isOverridden={isOverridden}
          activeBreakpoint={activeBreakpoint}
          bindingListProps={{
              searchQuery: bindingSearch,
              onSearchChange: setBindingSearch,
              onSelectNode: onSelect,
              onUpdateBindings: handleCompPropChange,
              onUnbind: handleUnbind
          }}
      />
  )};

  const existingBehaviorTypes = new Set(primaryNode.attachedComponents?.map(c => c.type) || []);
  const availableBehaviors = Object.values(BEHAVIORS).filter(b => !existingBehaviorTypes.has(b.id));

  return (
    <div className="flex flex-col h-full bg-admin_paper border-l border-admin_bordered overflow-y-auto font-sans text-admin_text relative">
      {/* Binding Popup */}
      <BindingPopup
          popup={bindingPopup}
          onBind={handleBind}
          onUnbind={() => handleUnbind()}
          onClose={() => setBindingPopup(null)}
      />

      <div className="p-4 border-b border-admin_bordered bg-admin_canvas flex justify-between items-center">
        <h2 className="font-bold text-admin_text text-sm">{primaryNode.label} <span className="text-gray-400 font-normal text-xs">({primaryNode.type})</span></h2>
        <div className="flex gap-2 items-center">
             {selectedNodes.length === 1 && !(isPrefab || isVirtual) && (
                <button onClick={() => onMakePrefab(selectedNodes.map(n => n.id), primaryNode.label || "New Prefab")} title="Create Prefab" className="text-blue-500 hover:bg-blue-50 p-1 rounded"><Icons.BoxSelect className="w-3.5 h-3.5"/></button>
             )}
            <button onClick={(e) => {e.stopPropagation(); onDelete(selectedNodes.map(n => n.id))}} className="text-gray-400 hover:text-red-500 p-1 rounded"><Icons.Trash className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      <PrefabPanel
        primaryNode={primaryNode}
        prefabs={prefabs}
        onOpenPrefab={onOpenPrefab}
        onUpdatePrefab={onUpdatePrefab}
        onBreakPrefab={onDetachPrefab}
      />

      <ResponsivePanel
          primaryNode={primaryNode}
          selectedNodes={selectedNodes}
          activeBreakpoint={activeBreakpoint}
          onChange={onChange}
          onSelectBreakpoint={onBreakpointChange}
          onViewportWidthChange={onViewportWidthChange}
      />

      <PropertySection key={`rect-${primaryNode.id}`} title="Rect Transform" defaultOpen={false}>
          <RectTransform
              primaryNode={activeBreakpoint === 'default' ? rawPrimaryNode : primaryNode}
              parentSize={effectiveParentSize}
              onChange={handleResponsiveChange}
              selectedNodes={selectedNodes}
              renderField={renderFieldWrapper}
              settings={settings}
              activeNodes={activeNodes}
              containerNode={containerNode}
              canvasRef={canvasRef}
              canvasScale={canvasScale}
              activeBreakpoint={activeBreakpoint}
              visualBreakpoint={effectiveBreakpoint}
          />
      </PropertySection>

      {/* Render Data Variables if Instance */}
      <InstanceVariables
          primaryNode={primaryNode}
          isPrefab={isPrefab}
          prefabs={prefabs}
          onPropChange={handlePropChange}
      />

      <PropertySection key={`styling-${primaryNode.id}`} title="Styling" defaultOpen={false}>
          {/* Styling Tabs */}
          <div className="flex text-[10px] font-bold border-b border-slate-100 mb-3">
              <button
                onClick={() => setStylingTab('variant')}
                className={`flex-1 py-1.5 border-b-2 transition-colors ${stylingTab === 'variant' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
              >
                  VARIANT
              </button>
              <button
                onClick={() => setStylingTab('object')}
                className={`flex-1 py-1.5 border-b-2 transition-colors ${stylingTab === 'object' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
              >
                  OBJECT
              </button>
          </div>

          {stylingTab === 'variant' && (
              <div>
                  {renderFieldWrapper(
                      'Style Variants',
                      primaryNode.props.variant,
                      (v) => handlePropChange('variant', v),
                      'variant_list',
                      undefined,
                      undefined,
                      undefined,
                      undefined,
                      undefined, // defId
                      primaryNode.props.disabledVariants || [],
                      (v) => handlePropChange('disabledVariants', v)
                  )}
              </div>
          )}

          {stylingTab === 'object' && (
              <div>
                  {/* Render active styles */}
                  {Object.entries(OBJECT_STYLES).map(([key, def]) => {
                      const hasBaseValue = rawPrimaryNode.props[key] !== undefined;
                      const hasOverride = rawPrimaryNode.props?.responsiveOverrides?.[activeBreakpoint]?.[key] !== undefined;

                      if (activeBreakpoint === 'default') {
                          // In Base mode, only show properties that exist in base
                          if (!hasBaseValue) return null;
                          const propertyValue = rawPrimaryNode.props[key];

                          return (
                              <div key={key} className="relative group">
                                  {renderFieldWrapper(
                                      def.label,
                                      propertyValue,
                                      (v) => handlePropChange(key, v),
                                      def.type,
                                      def.options,
                                      key
                                  )}
                                  <button
                                    onClick={() => handlePropChange(key, undefined)} // Remove property
                                    className="absolute top-0 right-0 p-1 text-red-300 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                    title="Remove Property"
                                  >
                                      <Icons.Remove className="w-3 h-3" />
                                  </button>
                              </div>
                          );
                      }

                      // For specific breakpoints (XS, SM, etc.)
                      // Show property if it exists in base OR has an override in this breakpoint
                      if (!hasBaseValue && !hasOverride) return null;

                      // Show the override value if it exists, otherwise show the base value
                      // DO NOT use primaryNode.props[key] (resolved value) as it may contain responsive prefixes
                      const overrideValue = rawPrimaryNode.props?.responsiveOverrides?.[activeBreakpoint]?.[key];
                      const propertyValue = overrideValue !== undefined ? overrideValue : rawPrimaryNode.props[key];

                      return (
                          <div key={key} className="relative group">
                              {renderFieldWrapper(
                                  def.label,
                                  propertyValue,
                                  (v) => handlePropChange(key, v),
                                  def.type,
                                  def.options,
                                  key
                              )}
                              <button
                                onClick={() => handlePropChange(key, undefined)} // Remove property
                                className="absolute top-0 right-0 p-1 text-red-300 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                title="Remove Property"
                              >
                                  <Icons.Remove className="w-3 h-3" />
                              </button>
                          </div>
                      );
                  })}

                  {/* Add Property Dropdown */}
                  <div className="relative mt-2">
                      <button
                        onClick={() => {
                            setShowAddObjectStyle(!showAddObjectStyle);
                            setObjectStyleSearch('');
                        }}
                        className="w-full text-xs py-1 border border-dashed border-slate-300 rounded text-slate-400 hover:text-blue-500 hover:border-blue-300 flex items-center justify-center gap-1"
                      >
                          <Icons.Plus className="w-3 h-3"/> Add Property
                      </button>
                      {showAddObjectStyle && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 shadow-xl rounded z-50 max-h-60 overflow-y-auto p-1">
                               <input
                                    autoFocus
                                    className="w-full text-xs p-1.5 border-b mb-1 outline-none sticky top-0 bg-white"
                                    placeholder="Search properties..."
                                    value={objectStyleSearch}
                                    onChange={e => setObjectStyleSearch(e.target.value)}
                               />
                               <div className="space-y-0.5">
                                   {Object.entries(OBJECT_STYLES)
                                       .filter(([key, def]) => {
                                           // Check if property exists in base OR current breakpoint override
                                           const existsInBase = rawPrimaryNode.props[key] !== undefined;
                                           const existsInOverride = activeBreakpoint !== 'default' &&
                                               rawPrimaryNode.props?.responsiveOverrides?.[activeBreakpoint]?.[key] !== undefined;
                                           const exists = existsInBase || existsInOverride;

                                           return !exists &&
                                               (def.label.toLowerCase().includes(objectStyleSearch.toLowerCase()) || key.toLowerCase().includes(objectStyleSearch.toLowerCase()));
                                       })
                                       .map(([key, def]) => (
                                           <button
                                                key={key}
                                                onClick={() => {
                                                    // Use the defined default (never undefined to avoid deletion logic)
                                                    handlePropChange(key, def.default);
                                                    setShowAddObjectStyle(false);
                                                    setObjectStyleSearch('');
                                                }}
                                                className="w-full text-left px-2 py-1.5 text-xs hover:bg-blue-50 rounded flex justify-between items-center transition-colors"
                                           >
                                               <span>{def.label}</span>
                                               <span className="text-[9px] text-gray-300 font-mono">{key}</span>
                                           </button>
                                       ))
                                   }
                               </div>
                               {(() => {
                                   // Count available properties (check base OR current breakpoint override)
                                   const availableProps = Object.keys(OBJECT_STYLES).filter(k => {
                                       const existsInBase = rawPrimaryNode.props[k] !== undefined;
                                       const existsInOverride = activeBreakpoint !== 'default' &&
                                           rawPrimaryNode.props?.responsiveOverrides?.[activeBreakpoint]?.[k] !== undefined;
                                       return !existsInBase && !existsInOverride;
                                   });

                                   if (availableProps.length === 0) {
                                       return <div className="text-xs text-gray-400 italic text-center p-2">All styles added</div>;
                                   }

                                   const matchingProps = availableProps.filter(k =>
                                       OBJECT_STYLES[k].label.toLowerCase().includes(objectStyleSearch.toLowerCase()) ||
                                       k.toLowerCase().includes(objectStyleSearch.toLowerCase())
                                   );

                                   if (matchingProps.length === 0 && availableProps.length > 0) {
                                       return <div className="text-xs text-gray-400 italic text-center p-2">No matches found</div>;
                                   }

                                   return null;
                               })()}
                          </div>
                      )}
                  </div>
              </div>
          )}
      </PropertySection>

      <PropertySection key={`behaviors-${primaryNode.id}`} title="Behaviors" defaultOpen={false} noPadding={true} action={
             <div className="relative flex items-center gap-1">
                 {clipboardBehavior && (
                     <button onClick={(e) => {e.stopPropagation(); handlePasteBehavior()}} className="text-blue-500 hover:bg-blue-50 p-1 rounded" title="Paste Behavior"><Icons.Clipboard className="w-3.5 h-3.5"/></button>
                 )}
                 {availableBehaviors.length > 0 && (
                     <button onClick={(e) => {e.stopPropagation(); setShowAddComponent(!showAddComponent)}} className="text-blue-500 hover:bg-blue-50 p-1 rounded" title="Add Behavior"><Icons.Plus className="w-3.5 h-3.5"/></button>
                 )}
                 {showAddComponent && (
                     <div className="absolute right-0 top-6 w-48 bg-white border shadow-xl rounded z-50 max-h-60 overflow-y-auto">
                         {availableBehaviors.map(b => (
                             <button key={b.id} onClick={() => {handleAddComponent(b.id); setShowAddComponent(false)}} className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 flex gap-2 items-center">
                                 {b.icon && Icons[b.icon] && React.createElement(Icons[b.icon], {className:"w-3 h-3"})} {b.label}
                             </button>
                         ))}
                     </div>
                 )}
             </div>
          }>
              {(primaryNode.attachedComponents || []).length === 0 ? <div className="p-4 text-xs text-gray-400 italic">No behaviors attached.</div> : (
                  <div>
                      {primaryNode.attachedComponents.map(comp => {
                          const def = getBehavior(comp.type);
                          if (!def) return null;
                          return (
                            <BehaviorItem
                                key={`${primaryNode.id}-${comp.id}`}
                                comp={comp}
                                def={def}
                                onRemove={handleRemoveComponent}
                                onPropChange={handleCompPropChange}
                                renderField={renderFieldWrapper}
                                onCopy={handleCopyBehavior}
                                onToggleEnable={handleToggleEnable}
                            />
                          );
                      })}
                  </div>
              )}
      </PropertySection>

      <VariantEditorDialog
          isOpen={variantEditor.isOpen}
          componentType={variantEditor.componentType}
          initialKey={variantEditor.initialKey}
          previewNode={primaryNode}
          onClose={() => setVariantEditor({ isOpen: false, componentType: null })}
          onApply={handleApplyVariants}
          activeBreakpoint={activeBreakpoint}
      />
    </div>
  );
};

export default PropertyPanel;